
import { Box, Skeleton, Typography } from '@mui/material';
import { DataGrid, GridCallbackDetails, GridColDef, GridColumnMenu, GridColumnMenuColumnsItem, GridColumnMenuProps, GridColumnVisibilityModel, GridInputRowSelectionModel, GridPagination, GridRenderCellParams, GridRowParams, GridRowSelectionModel, GridSortItem, GridToolbarContainer, GridToolbarExport, GridTreeNodeWithRender, GridValidRowModel, gridPageCountSelector, useGridApiContext, useGridApiRef, useGridSelector } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import MuiPagination from '@mui/material/Pagination';
import { TablePaginationProps } from '@mui/material/TablePagination';
import { Dispatch, MutableRefObject, SetStateAction, useContext, useEffect, useState } from 'react';
import { CustomColumnOrder } from './CustomColumnOrder';
import { useTranslation } from 'react-i18next';
import { GridStateCommunity } from '@mui/x-data-grid/models/gridStateCommunity';
import { useFormatNumber } from '../../../hooks/useFormatNumber';
import { GridApiCommunity } from '@mui/x-data-grid/internals';
import "./DataTable.css";
import GenericPromises from '../../../api/GenericPromises';
import { Currency } from '../../../interfaces/Commons/currencies';
import { CompanyConfiguration } from '../../../interfaces/Security/companies';
import useSnackBar from '../../Commons/SnackBar/useSnackBar';
import { Spinner } from '../../Commons/Spinner/Spinner';
import { useFiltersColumns } from '../Components/useFiltersColumns';
import { useMenuStore } from '../../../store/AuthStore';
import { useGridColumnSortPreferencesStore, useGridColumnVisiblePreferencesStore, usePreferenceTypesStore } from '../../../store/Preferences/PreferencesStore';
import { useGridPreferences } from '../../../hooks/useGridPreferences';
import { AuthContext } from '../../../context/AuthContext';

interface MyComponentProps {
    entity: string;
    namePreferences: string;
    nameOrderColumns: string;
    entityId: string;
    columns: GridColDef<any>[];
    setColumns: Dispatch<SetStateAction<GridColDef<any>[]>>;
    data: readonly GridValidRowModel[];
    preferences: any;
    isChildren?: boolean;
    setOpenDialog?: Dispatch<SetStateAction<boolean>>;
    setDataRow?: Dispatch<SetStateAction<{}>>;
    selectRows?: boolean;
    hiddenHeaderCheckBox?: boolean;
    onRowSelectionModelChange?: ((rowSelectionModel: GridRowSelectionModel, details: GridCallbackDetails<any>) => void) | undefined;
    countColumns?: string[];
    preSelectedRows?: GridInputRowSelectionModel | undefined;
    conditionalOpenRow?: string | undefined;
    initFilter?: {
        columnfilter: string;
        value: string;
    }[];
    hideFooter?: boolean,
    hideShadow?: boolean,
    disableColumnMenu?: boolean,
    isLoading?: boolean,
    sortPreference?: any[];
}

export default function DataTableSticky(
    {
        entity,
        namePreferences,
        nameOrderColumns,
        entityId,
        columns,
        setColumns,
        data,
        preferences,
        isChildren,
        setOpenDialog,
        setDataRow,
        selectRows,
        onRowSelectionModelChange,
        hiddenHeaderCheckBox,
        countColumns,
        preSelectedRows,
        hideFooter = false,
        hideShadow = false,
        conditionalOpenRow,
        initFilter,
        disableColumnMenu = false,
        isLoading = false,
        sortPreference = undefined
    }: MyComponentProps) {
    const [t] = useTranslation("global");
    const navigate = useNavigate();
    const [dataLoaded, setDataLoaded] = useState(false);
    const [scrollEvent, setScrollEvent] = useState(false);
    const [dictionaryTotals, setDictionaryTotals] = useState<{ id: string; total: number }[]>([]);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const { setFormatNumberFromTable } = useFormatNumber();
    const { conditionalFilterForTotals } = useFiltersColumns();
    const [rowCountData, setRowCountData] = useState<any[]>([]);
    const [customGridColumnVisibilityModel, setCustomGridColumnVisibilityModel] = useState<GridColumnVisibilityModel>();
    const [customGridColumnSortModel, setCustomGridColumnSortModel] = useState<GridSortItem[]>([]);
    const apiRef1 = useGridApiRef() as MutableRefObject<GridApiCommunity>;
    const apiRef2 = useGridApiRef() as MutableRefObject<GridApiCommunity>;
    const { GenericGetResource, GenericGetResourceGeneric, GenericPostResource, GenericPutResource } = GenericPromises();
    const [localPreference, setLocalPreference] = useState({})
    const { SnackbarComponent, showSnackBar } = useSnackBar();
    const { user } = useContext(AuthContext);
    const { menuState } = useMenuStore();
    const { getPreferenceTypeIdByPreferenceName } = usePreferenceTypesStore();
    const { findResourceIdByRoute } = useGridPreferences();
    const { getColumnVisualPreferenceByResourceId, updateColumnVisiblePreference } = useGridColumnVisiblePreferencesStore();
    const { getColumnSortPreferenceByResourceId, updateColumnSortPreference } = useGridColumnSortPreferencesStore();

    const onSelectedRow = (row: any) => {
        if (conditionalOpenRow && (row[conditionalOpenRow] !== null && row[conditionalOpenRow] !== undefined)) {
            if (isChildren) {
                if (setOpenDialog !== undefined && setDataRow !== undefined) {
                    setDataRow(row);
                    setOpenDialog(true);
                }
            } else {
                navigate("view", { state: { row: row } });
            }
        }
        else if (!conditionalOpenRow) {
            if (isChildren) {
                if (setOpenDialog !== undefined && setDataRow !== undefined) {
                    setDataRow(row);
                    setOpenDialog(true);
                }
            } else {
                navigate("view", { state: { row: row } });
            }
        }
    };

    function Pagination({
        page,
        onPageChange,
        className,
    }: Pick<TablePaginationProps, 'page' | 'onPageChange' | 'className'>) {
        const apiRef = useGridApiContext();
        const pageCount = useGridSelector(apiRef, gridPageCountSelector);

        return (
            <MuiPagination
                color="primary"
                variant='outlined'
                sx={{
                    '& .MuiPaginationItem-root:hover': {
                        color: 'secondary',
                        backgroundColor: 'primary',
                        border: 'none',
                    }
                }}
                className={className}
                count={pageCount}
                page={page + 1}
                onChange={(event, newPage) => {
                    onPageChange(event as any, newPage - 1);
                }}
            />
        );
    }

    interface GridTotalsProps {
        myColumns: GridColDef[];
        totals: any[];
        entityId: string;
    }

    function GridTotals({
        myColumns,
        totals,
        entityId
    }: GridTotalsProps) {

        return (
            <Box sx={{
                height: '40px',
                '& .MuiDataGrid-root': {
                    height: '40px !important',
                    '& .MuiDataGrid-virtualScroller': {
                        margin: '0 !important',
                        padding: '0 !important',
                        overflow: 'hidden !important'
                    }
                },
                '& .MuiDataGrid-row:hover': {
                    backgroundColor: 'transparent !important',
                },
                '& .MuiDataGrid-cell': {
                    pointerEvents: 'none !important',
                },
                '& .MuiDataGrid-cell:focus': {
                    outline: 'none !important',
                },
                '& .MuiDataGrid-virtualScroller': {
                    overflow: 'hidden !important',
                },
            }}>
                <DataGrid
                    apiRef={apiRef2}
                    density="compact"
                    rows={totals}
                    getRowId={(row) => row[entityId]}
                    columns={myColumns}
                    checkboxSelection={selectRows}
                    disableRowSelectionOnClick
                    hideFooter
                    hideFooterPagination
                    hideFooterSelectedRowCount
                    columnVisibilityModel={customGridColumnVisibilityModel}
                    onColumnVisibilityModelChange={(e) => SetCustomVisualPreferences(e)}
                    onRowSelectionModelChange={onRowSelectionModelChange}
                    onPaginationModelChange={handlePaginationModelChange}
                    onStateChange={() => onChangeGrid()}
                    sortModel={customGridColumnSortModel}
                    initialState={{
                        pagination: { paginationModel: { pageSize: rowsPerPage } },
                        columns: {
                            columnVisibilityModel: localPreference,
                        },
                        sorting: {
                            sortModel: [
                                { field: 'rate_date', sort: 'desc' },
                            ]
                        }
                    }}
                    sx={{
                        "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer": {
                            display: 'none !important'
                        },
                        '& .MuiDataGrid-checkboxInput': {
                            display: 'none !important'
                        },
                        '& .MuiDataGrid-virtualScroller': {
                            minHeight: '35px !important',
                            maxHeight: '35px !important',
                            overflow: 'hidden !important',
                            marginTop: '0 !important',
                            padding: '0 !important'
                        },
                        '& .MuiDataGrid-columnHeader': {
                            display: 'none'
                        },
                        '& .MuiDataGrid-columnHeaders': {
                            display: 'none'
                        },
                        '& .MuiDataGrid-row': {
                            maxHeight: '20px !important',
                            minHeight: '20px !important'
                        },
                        '& .MuiDataGrid-cell': {
                            padding: '2px 8px !important',
                            lineHeight: '1.2 !important',
                            fontSize: '0.875rem !important'
                        }
                    }}
                />
            </Box>
        );
    }

    function CustomPagination(props: any) {
        const apiRef = useGridApiContext();
        const [myColumns, setMyColumns] = useState<GridColDef<any>[]>([]);
        const [dataTotalsLoaded, setDataTotalsLoaded] = useState(false);
        const selectedRows = useGridSelector(apiRef, (state: GridStateCommunity) =>
            state.rowSelection
        );
        const [isSelectedRows, setIsSelectedRows] = useState(false);

        const SumRowsSelected = () => {
            let totals: { id: string, total: number }[] = [];
            if (countColumns && selectRows && data) {
                const totalsObj: { [key: string]: number } = {};
                countColumns.forEach(column => {
                    totalsObj[column] = 0;
                });

                selectedRows.forEach((rowIndex: any) => {
                    const rowData = data.find((element => element[entityId] === rowIndex));
                    if (rowData) {
                        countColumns.forEach(column => {
                            totalsObj[column] += rowData[column];
                        });
                    }
                });

                totals = Object.entries(totalsObj).map(([id, total]) => ({ id, total }));
            }
            return totals;
        }

        useEffect(() => {
            if (columns.length > 0 && !dataTotalsLoaded) {
                let myAuxColumns: GridColDef<any>[] = columns.map(column => ({ ...column }));

                for (let i = 0; i < myAuxColumns.length; i++) {
                    const element = myAuxColumns[i];
                    //oculta todas las columnas que no esten en countColumns quitandoles el rednerCell y el valueGetter
                    if (countColumns && !(countColumns.some((column) => column === element.field))) {
                        element.renderCell = undefined;
                        element.valueGetter = undefined;
                        element.renderEditCell = undefined;
                        element.valueParser = undefined;
                        element.valueSetter = undefined
                        element.type = undefined;
                    }
                }

                setMyColumns(myAuxColumns);
            }
        }, [columns]);

        useEffect(() => {
            if (myColumns.length > 0 && !dataTotalsLoaded) {
                setDataTotalsLoaded(true);
            }
        }, [myColumns])

        useEffect(() => { }, [localPreference])

        useEffect(() => {
            if (selectedRows.length > 0 && countColumns) {
                setIsSelectedRows(true);
            }
            else {
                setIsSelectedRows(false);
            }
        }, [selectedRows, countColumns])

        return (
            <Box sx={{
                position: 'sticky',
                bottom: 0,
                zIndex: 3,
                backgroundColor: 'background.paper',
                borderTop: '1px solid',
                borderColor: 'divider',
                width: '100%',
                overflow: 'hidden'
            }}>
                {countColumns && countColumns.length > 0 && (
                    <GridTotals
                        myColumns={myColumns}
                        totals={rowCountData}
                        entityId={entityId}
                    />
                )}
                {isSelectedRows && (
                    <Box sx={{
                        display: 'flex',
                        borderTop: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Box display="flex" gap={2} sx={{ overflowX: "auto", maxWidth: "100%", whiteSpace: "nowrap" }}>
                            <Typography variant="body2">
                                {t("generic.grid-table.count-rows")}: {selectedRows.length}
                            </Typography>
                            {SumRowsSelected().map((total) => (
                                <Typography key={total.id} variant="body2">
                                    {total.id === "total_company_currency_amount"
                                        ? `Total: ${setFormatNumberFromTable(total.total)}`
                                        : (t(`${entity.toLowerCase()}.fields.${total.id}`) !== `${entity.toLowerCase()}.fields.${total.id}`) ?
                                            `${t(`${entity.toLowerCase()}.fields.${total.id}`)}: ${setFormatNumberFromTable(total.total)}`
                                            : ""
                                    }
                                </Typography>
                            ))}
                        </Box>
                    </Box>
                )}
                <Box
                    sx={{
                        justifyContent: "flex-end",
                        display: "flex",
                        borderTop: (!(isSelectedRows)) ? '1px solid' : '',
                        borderColor: (!(isSelectedRows)) ? 'divider' : '',
                    }}
                >
                    <CustomToolbar />
                    <GridPagination
                        labelRowsPerPage={t("generic.grid-table.rows-per-page")}
                        ActionsComponent={Pagination}
                        sx={{
                            '& .MuiTablePagination-toolbar': {
                                padding: 0,
                                minHeight: '64px !important'
                            }
                        }}
                        {...props}
                    />
                </Box>
            </Box>
        );
    }

    function CustomToolbar() {
        return (
            <>
                <GridToolbarContainer
                    sx={{
                        position: 'sticky',
                        top: 40,
                        zIndex: 2,
                        backgroundColor: 'background.paper',
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    <GridToolbarExport
                        printOptions={{ disableToolbarButton: true }}
                        csvOptions={{
                            fileName: `${entity}`,
                            utf8WithBom: true,
                        }}
                    />
                </GridToolbarContainer>
            </>
        );
    }

    function CustomColumnsItems({ ...props }) {
        return <div>
            <CustomColumnOrder columns={columns} setColumns={setColumns} nameOrderColumns={nameOrderColumns} />
            <GridColumnMenuColumnsItem colDef={props.colDef} onClick={props.onClick} />
        </div>
    }

    function CustomColumnMenuComponent(props: GridColumnMenuProps) {
        return (
            <GridColumnMenu
                {...props}
                slots={{
                    columnMenuColumnsItem: CustomColumnsItems,
                }}
            />
        );
    }

    const ParseColumnsToPreferenceVisualValue = (myVisualColumnsPreferences: GridColumnVisibilityModel) => {
        const arrayOfObjects = Object.keys(myVisualColumnsPreferences).map(key => {
            return { [key]: myVisualColumnsPreferences[key] };
        });
        return arrayOfObjects;
    }

    const SetCustomSortPreferences = (mysortColumnPreferences: GridSortItem[]) => {
        if (dataLoaded) {
            setCustomGridColumnSortModel(mysortColumnPreferences);
            if (sortPreference && namePreferences.includes('/', 0)) {
                // get resource_id by route - validate if has id(put) if not post
                let myResourceId = findResourceIdByRoute(menuState, nameOrderColumns);
                // validate order preference get id from state
                let myPreference = getColumnSortPreferenceByResourceId(myResourceId ?? 0);
                // get preferenceTypeId by Name
                let myPreferenceTypeId = getPreferenceTypeIdByPreferenceName("GridColumnsSorting");
                // translate preferences values to backend like PreferenceSortValue
                let myPreferenceValue = mysortColumnPreferences;
                if (myPreference != undefined && myPreference.preference_id) {
                    let myData = {
                        preference_value: JSON.stringify(myPreferenceValue),
                    }
                    // put
                    GenericPutResource(`/preferences/${myPreference.preference_id}`, myData)
                        .then((responsePut) => {
                            // update state
                            let myObject = responsePut.data;
                            updateColumnSortPreference(myObject)
                        })
                        .catch((error) => {
                            throw error;
                        });
                }
                else {
                    let myData = {
                        preference_value: JSON.stringify(myPreferenceValue),
                        resource_id: myResourceId,
                        user_company_id: user?.user_company_id,
                        preference_type_id: myPreferenceTypeId,
                    }
                    // post
                    GenericPostResource(`/preferences`, myData)
                        .then((responsePost) => {
                            // update state
                            let myObject = responsePost.data;
                            updateColumnSortPreference(myObject)
                        })
                        .catch((error) => { throw error; });
                }
            }
            else {
                localStorage.setItem(namePreferences, JSON.stringify(mysortColumnPreferences));
            }
        }
    }

    const SetCustomVisualPreferences = (myVisualColumnsPreferences: GridColumnVisibilityModel) => {
        if (namePreferences.includes('/', 0)) {
            // get resource_id by route - validate if has id(put) if not post
            let myResourceId = findResourceIdByRoute(menuState, nameOrderColumns);
            // validate order preference get id from state
            let myPreference = getColumnVisualPreferenceByResourceId(myResourceId ?? 0);
            // get preferenceTypeId by Name
            let myPreferenceTypeId = getPreferenceTypeIdByPreferenceName("GridColumnsVisibility");
            // translate preferences values to backend like PreferenceVisualValue
            let myPreferenceValue = ParseColumnsToPreferenceVisualValue(myVisualColumnsPreferences);
            if (myPreference != undefined && myPreference.preference_id) {
                let myData = {
                    preference_value: JSON.stringify(myPreferenceValue),
                }
                // put
                GenericPutResource(`/preferences/${myPreference.preference_id}`, myData)
                    .then((responsePut) => {
                        // update state
                        let myObject = responsePut.data;
                        updateColumnVisiblePreference(myObject)
                        setLocalPreference(myVisualColumnsPreferences)
                    })
                    .catch((error) => { throw error; });
            }
            else {
                let myData = {
                    preference_value: JSON.stringify(myPreferenceValue),
                    resource_id: myResourceId,
                    user_company_id: user?.user_company_id,
                    preference_type_id: myPreferenceTypeId,
                }
                // post
                GenericPostResource(`/preferences`, myData)
                    .then((responsePost) => {
                        // update state
                        let myObject = responsePost.data;
                        updateColumnVisiblePreference(myObject)
                        setLocalPreference(myVisualColumnsPreferences)
                    })
                    .catch((error) => { throw error; });
            }
        }
        else {
            localStorage.setItem(namePreferences, JSON.stringify(myVisualColumnsPreferences));
            setCustomGridColumnVisibilityModel(myVisualColumnsPreferences);
            setLocalPreference(myVisualColumnsPreferences)
        }
    };

    const SetRowCount = (dictionaryTotals: any) => {
        if (data.length > 0) {
            const totalsRow = columns.reduce((acc, col) => {
                const total = dictionaryTotals.find((t: any) => t.id === col.field);
                acc[col.field] = total?.total || null;
                return acc;
            }, { [entityId]: data.length + 1 });

            setRowCountData([totalsRow]);
        }
    };

    useEffect(() => {
        const loadData = async () => {
            if (preferences && typeof preferences === 'object' && Object.keys(preferences).length > 1) {
                setLocalPreference(preferences);
            } else {
                const defaultPreferences = {
                    "last_update_user": false,
                    "creation_date": false,
                    "last_update_date": false
                };
                setLocalPreference(defaultPreferences);
            }
            if (sortPreference && typeof sortPreference === 'object' && Object.keys(sortPreference).length > 0) {
                await setCustomGridColumnSortModel(sortPreference);
            }
            let myNewColumns: GridColDef<any>[] = [];
            for (let i = 0; i < columns.length; i++) {
                const element = columns[i];
                if (element.minWidth === undefined) {
                    element.minWidth = 200;
                }
                if (element.type === "date") {
                    element.renderCell = (params) => {
                        try {
                            if (params.value && !isNaN(Date.parse(params.value as string))) {
                                const date = params.value as Date;
                                return new Intl.DateTimeFormat('es-ES', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric'
                                }).format(date);
                            }
                            return undefined
                        }
                        catch {
                            return undefined
                        }
                    }
                }
                myNewColumns.push(element);
            }
            setDataLoaded(true);
            if (countColumns && countColumns.length > 0) {
                // GET CURRENCIES AND COMPANY CONFIGURATION INFO
                let myComboCurrencies: Currency[];
                let myCompanyConfiguration: CompanyConfiguration;
                let myDefaultCurrency: Currency | undefined;
                let myPromises = [
                    GenericGetResource(`/currencies`),
                    GenericGetResourceGeneric(`/companyconfigurations/bycompanyid/${1}`, "/gcompanyconfigurations"),
                ];
                Promise.all(myPromises)
                    .then(async (responses) => {
                        // Match currencies target
                        myComboCurrencies = responses[0].data.data;
                        myCompanyConfiguration = responses[1].data.data[0];
                        myDefaultCurrency = myComboCurrencies.find((item) => item.currency_id === myCompanyConfiguration.company_currency_id);
                        // get columns to sum totals
                        let myColumnTotalAmount = "";
                        let myColumnCurrencyCode = "";
                        switch (entityId) {
                            case "sales_invoice_id":
                            case "purchase_invoice_id":
                            case "cancelled_sales_invoice_id":
                            case "sales_preorder_id":
                            case "sales_order_id":
                                {
                                    myColumnTotalAmount = "total_amount";
                                    myColumnCurrencyCode = "currency_code";
                                    break;
                                }
                            case "receipt_detail_id":
                                {
                                    myColumnTotalAmount = "=current_receipt_status_id=1?amount_outstanding:payment_amount";
                                    myColumnCurrencyCode = "related_currency_code";
                                    break;
                                }
                            case "payment_detail_id":
                                {
                                    myColumnTotalAmount = "=current_payment_status_id=1?amount_outstanding:payment_amount";
                                    myColumnCurrencyCode = "related_currency_code";
                                    break;
                                }
                            default:
                                break;
                        }
                        // exchange rate today
                        // get currency id 
                        let myCurrencyUSD = myComboCurrencies.find((item) => item.currency_code.toUpperCase() === "USD");
                        let myExchangeRate = 1;
                        await GenericGetResource(`/exchangeratehistory/bycurrencyid/${(myCurrencyUSD?.currency_id ?? 0)}`)
                            .then((responseExchangeRate) => {
                                // get currency from date today
                                const today = new Date().toISOString().split("T")[0];
                                myExchangeRate = responseExchangeRate.data.data.find((rate: any) => rate.rate_date.startsWith(today))?.rate || 1;
                            });

                        const processedTotals = countColumns.map(async (column) => {
                            let myTotals = 0;
                            if (column === "total_company_currency_amount") {
                                for (let i = 0; i < data.length; i++) {
                                    const element = data[i];
                                    let myFilterColumnTotalAmount = await conditionalFilterForTotals(myColumnTotalAmount, element);
                                    // load currencies value with exchange rate if it's different currency_id
                                    if (myDefaultCurrency?.currency_code === element[myColumnCurrencyCode.toString()]) {
                                        myTotals = myTotals + element[myFilterColumnTotalAmount.toString()];
                                        element.total_company_currency_amount = element[myFilterColumnTotalAmount.toString()];
                                    }
                                    // else add total amount to value
                                    else {
                                        // si el currency de salesinvoices es MXN multiplicar
                                        if (element[myColumnCurrencyCode.toString()] === "MXN") {
                                            // dividir el monto entre el tipo de cambio
                                            myTotals = myTotals + (element[myFilterColumnTotalAmount.toString()] / (myExchangeRate > 0 ? myExchangeRate : 1));
                                            element.total_company_currency_amount = (element[myFilterColumnTotalAmount.toString()] / (myExchangeRate > 0 ? myExchangeRate : 1));
                                        }
                                        else if (element[myColumnCurrencyCode.toString()] === "USD") {
                                            // multiplicar el tippo de cambio
                                            myTotals = myTotals + (element[myFilterColumnTotalAmount.toString()] * myExchangeRate);
                                            element.total_company_currency_amount = (element[myFilterColumnTotalAmount.toString()] * myExchangeRate);
                                        }
                                    }
                                }
                            }
                            else {
                                myTotals = data.reduce((acc, element) => acc + (element[column] || 0), 0);
                            }

                            return {
                                id: column,
                                total: myTotals,
                            }
                        });
                        Promise.all(processedTotals)
                            .then((resolvedTotals) => {
                                setDictionaryTotals(resolvedTotals);
                                SetRowCount(resolvedTotals);
                            });
                    })
                    .catch((error) => {
                        showSnackBar(error.message, 'error');
                    });
            }
        }

        loadData();
    }, [data]);

    const handlePaginationModelChange = (params: any) => {
        setRowsPerPage(params.pageSize);
        SetRowCount(dictionaryTotals);
    };

    const scrollerTables = (tableprincipal: MutableRefObject<GridApiCommunity>, tablesecondary: MutableRefObject<GridApiCommunity>) => {
        try {
            if (tableprincipal.current.getScrollPosition && tablesecondary.current.scroll != undefined) {
                let tablescroll = tableprincipal.current.getScrollPosition();
                tablesecondary.current.scroll(tablescroll);
            }
        }
        catch (e) { }
    }

    const onChangeGrid = () => {
        const handleScroll = () => {
            scrollerTables(apiRef1, apiRef2);
        };

        const api = apiRef1.current;
        if (api.subscribeEvent !== undefined && scrollEvent !== true) {
            api.subscribeEvent('scrollPositionChange', handleScroll);
            setScrollEvent(true);
        }
    };

    const getRowClassName = (params: any) => {
        if (params.row.product_service_key === '84111506') {
            return 'yellowRow';
        }
        else if (params.row.is_accounted || params.row.receipt_id || params.row.payment_id) {
            return 'textBold';
        }
        else if (params.row.status_color) {
            return params.row.status_color;
        }
        return '';
    };

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                '& .MuiDataGrid-root': {
                    flex: 1,
                    border: 'none',
                    '& .MuiDataGrid-columnHeaders': {
                        position: 'sticky',
                        top: 0,
                        zIndex: 3,
                        backgroundColor: 'rgba(150, 150, 150, 0.27)',
                        '& .MuiDataGrid-columnHeaderTitleContainer': {
                            fontWeight: 'bold'
                        }
                    },
                    '& .MuiDataGrid-virtualScroller': {
                        marginTop: '0!important',
                    },
                    '& .MuiDataGrid-columnSeparator': {
                        display: 'none',
                    },
                }
            }}>
            {!dataLoaded && <Spinner isBox={false} />}
            {dataLoaded &&
                <DataGrid
                    apiRef={apiRef1}
                    loading={isLoading}
                    sx={{
                        '& .MuiDataGrid-main': {
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%'
                        },
                        '& .MuiDataGrid-row:hover': {
                            color: 'primary.main',
                        },
                        '& .MuiDataGrid-cell:focus': {
                            outline: 'none',
                        },
                        overflow: 'hidden',
                        '& ::-webkit-scrollbar': {
                            width: '8px',
                            height: '8px'
                        },
                        '& ::-webkit-scrollbar-thumb': {
                            backgroundColor: 'divider',
                            borderRadius: '4px'
                        },
                        "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer": {
                            display: `${hiddenHeaderCheckBox ? "none" : ""}`,
                        },
                        boxShadow: (hideShadow) ? 0 : 10,
                        '.MuiDataGrid-columnSeparator': {
                            display: 'none',
                        },
                        '& .MuiDataGrid-columnHeader--showColumnBorder': {
                            outline: 'none',
                            bordere: 'none'
                        },
                        ".MuiTablePagination-displayedRows, .MuiTablePagination-selectLabel": {
                            marginTop: ".5em",
                            marginBottom: ".5em",
                        },
                        '& .MuiDataGrid-selectedRowCount': {
                            display: 'none',
                        },
                        '& .MuiDataGrid-row': {
                            maxHeight: '20px !important'
                        },
                        height: 'calc(100% - 60px)',
                        '& .MuiDataGrid-columnHeader': {
                            maxWidth: 'none !important',
                            '&:not(:last-child)': {
                                borderRight: '1px solid #e0e0e0'
                            }
                        },
                        '& .MuiDataGrid-columnHeaders': {
                            overflow: 'visible !important',
                            zIndex: 5
                        },
                        '& .MuiDataGrid-virtualScroller': {
                            overflow: 'auto !important',
                            minHeight: '35px !important'
                        },
                    }}
                    localeText={{
                        toolbarExport: t("generic.grid-table.toolbar-export"),
                        toolbarExportCSV: t("generic.grid-table.toolbar-export-csv"),
                        columnMenuUnsort: t("generic.grid-table.column-menu-unsort"),
                        columnMenuSortAsc: t("generic.grid-table.column-menu-sort-asc"),
                        columnMenuSortDesc: t("generic.grid-table.column-menu-sort-desc"),
                        columnMenuFilter: t("generic.grid-table.column-menu-filter"),
                        columnMenuHideColumn: t("generic.grid-table.column-menu-hide-column"),
                        columnMenuShowColumns: t("generic.grid-table.column-menu-show-columns"),
                        columnMenuManageColumns: t("generic.grid-table.column-menu-manage-columns"),
                        filterOperatorContains: t("generic.grid-table.filter-operator-contains"),
                        filterOperatorEquals: t("generic.grid-table.filter-operator-equals"),
                        filterOperatorStartsWith: t("generic.grid-table.filter-operator-starts-with"),
                        filterOperatorEndsWith: t("generic.grid-table.filter-operator-ends-with"),
                        filterOperatorIsEmpty: t("generic.grid-table.filter-operator-is-empty"),
                        filterOperatorIsNotEmpty: t("generic.grid-table.filter-operator-is-not-empty"),
                        filterOperatorIsAnyOf: t("generic.grid-table.filter-operator-is-any-of"),
                    }}
                    density="compact"
                    getRowId={(row) => row[entityId]}
                    rows={data}
                    columns={columns}
                    onSortModelChange={(e) => SetCustomSortPreferences(e)}
                    sortModel={customGridColumnSortModel}
                    checkboxSelection={selectRows}
                    onColumnVisibilityModelChange={(e) => SetCustomVisualPreferences(e)}
                    onRowSelectionModelChange={onRowSelectionModelChange}
                    onRowDoubleClick={({ row }) => onSelectedRow(row)}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 50 } },
                        columns: {
                            columnVisibilityModel: localPreference,
                        },
                        sorting: {
                            sortModel: [
                                { field: 'rate_date', sort: 'desc' },
                            ]
                        },
                        filter: {
                            filterModel: {
                                items: (initFilter && initFilter.length > 0) ?
                                    initFilter.map(element => {
                                        return { field: element.columnfilter, operator: 'equals', value: element.value };
                                    })
                                    : []
                            },
                        },
                    }}
                    scrollbarSize={8}
                    rowSelectionModel={preSelectedRows}
                    slots={{ pagination: CustomPagination, columnMenu: CustomColumnMenuComponent, }}
                    pageSizeOptions={[50, 75, 100]}
                    disableColumnMenu={disableColumnMenu}
                    hideFooter={hideFooter}
                    onPaginationModelChange={handlePaginationModelChange}
                    disableRowSelectionOnClick
                    getRowClassName={getRowClassName}
                />
            }
            <SnackbarComponent />
        </Box>

    );
}